/**
 * Language Context
 *
 * Provides global language state that syncs with server-side user settings.
 * Follows the same pattern as ThemeContext for consistency.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUserSettings, useUpdateUserSettings } from '../services/api/queries/useUserSettings';
import i18n, { LanguageCode } from '../i18n/config';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => Promise<void>;
  isInitialized: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const DEFAULT_LANGUAGE: LanguageCode = 'en';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { data: userSettings, isLoading } = useUserSettings();
  const updateUserSettings = useUpdateUserSettings();
  const [isInitialized, setIsInitialized] = useState(false);

  // Load language from user settings when available
  useEffect(() => {
    if (!isLoading && userSettings) {
      const userLang = (userSettings.prefer_language || DEFAULT_LANGUAGE) as LanguageCode;
      if (userLang !== i18n.language) {
        i18n.changeLanguage(userLang);
      }
      setIsInitialized(true);
    }
  }, [userSettings, isLoading]);

  const setLanguage = async (lang: LanguageCode) => {
    try {
      // Update server-side preference
      await updateUserSettings.mutate({ prefer_language: lang });
      // Update i18n
      i18n.changeLanguage(lang);
    } catch (error) {
      console.error('Failed to save language preference:', error);
      // Still update local i18n even if server fails
      i18n.changeLanguage(lang);
    }
  };

  const language = (i18n.language as LanguageCode) || DEFAULT_LANGUAGE;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isInitialized }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * useLanguage hook
 *
 * Access language context in any component.
 * Throws error if used outside LanguageProvider.
 *
 * @example
 * const { language, setLanguage, isInitialized } = useLanguage();
 */
export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
